import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import type { AnyExtension, RemirrorEventListener } from 'remirror';
import { MentionAtomExtension, PlaceholderExtension } from 'remirror/extensions';
import type { InvalidContentHandler, RemirrorJSON } from '@remirror/core';
import { EditorComponent, Remirror, useRemirror, useRemirrorContext } from '@remirror/react';
import { getPlainTextFromDoc } from '@trinity/chat-components/components/Remirror/utils/get-plain-text';
import type { AtMentionContextValue } from './AtMentionContext';
import { AtMentionContext } from './AtMentionContext';
import { AtMentionDropdown } from './AtMentionDropdown';
import { EditorWrapper } from './RemirrorChatInput.styled';

export type RemirrorChatInputRef = {
  insertText: (text: string) => void;
  clearAndSetText: (text: string) => void;
  clearAndSetDoc: (doc: RemirrorJSON) => void;
};

type Props = {
  placeholder?: string;
  disabled?: boolean;
  onSubmit: () => void;
  onTextChange: (text: string) => void;
  onDocChange: (doc: RemirrorJSON) => void;
  mentionsEnabled?: boolean;
  atMentionContextValue?: AtMentionContextValue;
  topContent?: React.ReactNode;
};

type InsertTextCommandProps = {
  forwardedRef: React.Ref<RemirrorChatInputRef>;
};

const InsertTextCommands = ({ forwardedRef }: InsertTextCommandProps) => {
  const { commands, setContent } = useRemirrorContext();

  useImperativeHandle(
    forwardedRef,
    () => ({
      insertText: (text: string) => {
        commands.focus();
        commands.insertText(text);
      },
      clearAndSetText: (text: string) => {
        if (text) {
          setContent({
            type: 'doc',
            content: [{ type: 'paragraph', content: [{ type: 'text', text }] }],
          });
          commands.focus('end');
        } else {
          setContent({ type: 'doc', content: [{ type: 'paragraph' }] });
        }
      },
      clearAndSetDoc: (doc: RemirrorJSON) => {
        setContent(doc);
        commands.focus('end');
      },
    }),
    [commands, setContent]
  );

  return null;
};

export const RemirrorChatInput = forwardRef<RemirrorChatInputRef, Props>(function RemirrorChatInput(
  {
    placeholder,
    disabled,
    onSubmit,
    onTextChange,
    onDocChange,
    mentionsEnabled,
    atMentionContextValue,
    topContent,
  },
  ref
) {
  const innerRef = useRef<RemirrorChatInputRef>(null);
  const [editorFocused, setEditorFocused] = useState(false);

  const handleFocusCapture = useCallback(() => {
    setEditorFocused(true);
  }, []);

  const handleBlurCapture = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    // Keep focus true when moving within wrapper; close when focus leaves input area.
    if (e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    setEditorFocused(false);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      insertText: (text: string) => {
        innerRef.current?.insertText(text);
      },
      clearAndSetText: (text: string) => {
        innerRef.current?.clearAndSetText(text);
      },
      clearAndSetDoc: (doc: RemirrorJSON) => {
        innerRef.current?.clearAndSetDoc(doc);
      },
    }),
    []
  );

  const extensions = useCallback(() => {
    const base = [new PlaceholderExtension({ placeholder: placeholder ?? 'Type your question.' })];
    if (mentionsEnabled) {
      base.push(
        new MentionAtomExtension({
          matchers: [{ name: 'subject', char: '@', matchOffset: 0 }],
          extraAttributes: {
            entityType: { default: '' },
            link: { default: null },
          },
        }) as never
      );
    }
    return base;
  }, [placeholder, mentionsEnabled]);

  const onError: InvalidContentHandler = useCallback(({ json, invalidContent, transformers }) => {
    return transformers.remove(json, invalidContent);
  }, []);

  const { manager, state } = useRemirror({ extensions, onError });

  const handleChange: RemirrorEventListener<AnyExtension> = useCallback(
    (parameter) => {
      const json = parameter.state.doc.toJSON() as RemirrorJSON;
      onDocChange(json);
      onTextChange(getPlainTextFromDoc(json));
    },
    [onDocChange, onTextChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSubmit();
      }
    },
    [onSubmit]
  );

  return (
    <Remirror manager={manager} initialContent={state} onChange={handleChange} editable={!disabled}>
      <InsertTextCommands forwardedRef={innerRef} />
      <EditorWrapper
        onKeyDown={handleKeyDown}
        onFocusCapture={handleFocusCapture}
        onBlurCapture={handleBlurCapture}
      >
        {topContent}
        <EditorComponent />
        {mentionsEnabled && atMentionContextValue && (
          <AtMentionContext.Provider value={atMentionContextValue}>
            <AtMentionDropdown editorFocused={editorFocused} />
          </AtMentionContext.Provider>
        )}
      </EditorWrapper>
    </Remirror>
  );
});
